<!-- Media Accreditation Pass Form -->
<template>
    <page-loading-animation v-if="isLoading" :is-loading="isLoading" />

    <div v-else class="pa-4">
        <!--Instructions-->
        <div :class="$vuetify.breakpoint.width < 1000 ? 'd-flex flex-column' : 'd-flex align-start'">
            <!--Text-->
            <app-text :class="$vuetify.breakpoint.width < 1000 ? 'mb-4' : 'mr-4'" size="small">
                Update the Passes for this Organisation by selecting the Pass Status for each User. Alternatively, use the Passes Configurator for
                more advanced options.
            </app-text>

            <v-spacer />

            <!--Passes Configurator Button-->
            <app-btn
                @click.native="isSetAllDialogVisible = true"
                :class="$vuetify.breakpoint.width < 1000 ? 'align-self-end' : ''"
                label="Passes Configurator"
            />
        </div>

        <!--Selected User-->
        <form-section-title v-if="selectedUser?.userData?.entityId" class="mt-8" title="Selected User" />
        <div v-if="selectedUser?.userData?.entityId">
            <div class="d-flex align-center mt-4">
                <avatar class="mr-4" :file="selectedUser.fileData" :size="56" :user="selectedUser.userData" />

                <!--Name | Position | Status-->
                <div>
                    <!--Name-->
                    <app-text size="normal-bold">{{ selectedUser.userData.userName }}</app-text>

                    <!--Position-->
                    <app-text color="grey9" size="small">{{ selectedUser.userData.userPosition }}</app-text>

                    <!--Status - Shown as v-chips-->
                    <div>
                        <v-chip class="mr-2" :color="getStatusConfig(selectedUser.passData?.accreditationPassStatus).color" small text-color="white">
                            {{ selectedUser.passData?.accreditationPassStatus || 'Not Applied' }}
                        </v-chip>
                        <v-chip v-if="selectedUser?.passData?.accreditationPassPrinted" color="green" small text-color="white">Printed </v-chip>
                    </div>
                </div>

                <v-spacer />

                <!--Status Buttons-->
                <div class="d-flex align-center">
                    <div
                        v-for="status in statusOptions"
                        :key="status.value"
                        @click="handleStatusSelection(selectedUser, status.value)"
                        class="appWhite cursorPointer rounded-lg ml-4 pa-2"
                        :class="getUserStatus(selectedUser) === status.value && status.color"
                    >
                        <app-icon :color="getUserStatus(selectedUser) === status.value ? 'white' : status.color" :icon="status.icon" size="32" />
                    </div>
                </div>
            </div>

            <!--Add the holder type and organisation dropdowns for selected user-->
            <div class="d-flex align-center mt-4">
                <app-form-field
                    form-type="select"
                    class="pr-2"
                    :error="userErrors[selectedUser.userData.entityId]?.eventHolderType"
                    :error-message="userErrors[selectedUser.userData.entityId]?.eventHolderTypeErrorMessage"
                    :items="holderTypeOptions"
                    label="Rights Holder Type"
                    v-model="userHolderTypes[selectedUser.userData.entityId]"
                    style="width: 50%"
                />

                <app-form-field
                    form-type="select"
                    class="pl-2"
                    :error="userErrors[selectedUser.userData.entityId]?.organisation"
                    :error-message="userErrors[selectedUser.userData.entityId]?.organisationErrorMessage"
                    :items="getAvailableOrganisations(selectedUser.userData)"
                    label="Organisation"
                    v-model="userOrganisations[selectedUser.userData.entityId]"
                    style="width: 50%"
                />
            </div>
        </div>

        <!-- Users -->
        <form-section-title class="mt-8" description="Other Users from your Organisation." title="Other Users" />
        <app-text v-if="!computedTableData.length" class="text-center" color="grey9"> There are no other Users that have applied </app-text>
        <div v-for="item in computedTableData" :key="`user-${item.userData.entityId}`">
            <div class="d-flex align-center">
                <avatar class="mr-4" :file="item.fileData" :size="56" :user="item.userData" />

                <!--Name | Position | Status-->
                <div>
                    <!--Name-->
                    <app-text size="normal-bold">{{ item.userData.userName }}</app-text>

                    <!--Position-->
                    <app-text color="grey9" size="small">{{ item.userData.userPosition }}</app-text>

                    <!--Status-->
                    <div>
                        <v-chip class="mr-2" :color="getStatusConfig(item.passData?.accreditationPassStatus).color" small text-color="white">
                            {{ item.passData?.accreditationPassStatus || 'Not Applied' }}
                        </v-chip>
                        <v-chip v-if="item?.passData?.accreditationPassPrinted" color="green" small text-color="white">Printed </v-chip>
                    </div>
                </div>

                <v-spacer />

                <!--Status Buttons-->
                <div class="d-flex align-center">
                    <div
                        v-for="status in statusOptions"
                        :key="status.value"
                        @click="handleStatusSelection(item, status.value)"
                        class="appWhite cursorPointer rounded-lg ml-4 pa-2"
                        :class="getUserStatus(item) === status.value && status.color"
                    >
                        <app-icon :color="getUserStatus(item) === status.value ? 'white' : status.color" :icon="status.icon" size="32" />
                    </div>
                </div>
            </div>

            <!--Rights Holder Type and Organisation Selection-->
            <div v-if="item.passData?.accreditationPassStatus || isSelected(item)" class="d-flex align-center mt-4">
                <app-form-field
                    form-type="select"
                    class="pr-2"
                    :error="userErrors[item.userData.entityId]?.eventHolderType"
                    :error-message="userErrors[item.userData.entityId]?.eventHolderTypeErrorMessage"
                    :items="holderTypeOptions"
                    label="Rights Holder Type"
                    v-model="userHolderTypes[item.userData.entityId]"
                    style="width: 50%"
                />

                <app-form-field
                    form-type="select"
                    class="pl-2"
                    :error="userErrors[item.userData.entityId]?.organisation"
                    :error-message="userErrors[item.userData.entityId]?.organisationErrorMessage"
                    :items="getAvailableOrganisations(item.userData)"
                    label="Organisation"
                    v-model="userOrganisations[item.userData.entityId]"
                    style="width: 50%"
                />
            </div>

            <v-divider class="greyD my-4" />
        </div>

        <!--Save Button - Fixed Position-->
        <div class="fixed-save-button">
            <app-btn @click.native="savePasses" color="green" :disabled="computedIsButtonDisabled" icon="save" label="Save" />
        </div>

        <!--Dialogs ------------------------------------------------------------------------------------------------ -->
        <v-dialog v-if="isSetAllDialogVisible" max-width="600" v-model="isSetAllDialogVisible">
            <div class="appGrey rounded-lg pa-4">
                <!--Title-->
                <app-text color="primary" size="medium-bold">Set All Configurator</app-text>

                <v-divider class="mt-4" />

                <!--Body Text-->
                <app-text class="mt-4"> Select the Pass status change you wish to apply from the configurator below. </app-text>

                <!--'Please Note' Message-->
                <div class="d-flex align-center mt-4">
                    <app-icon class="mr-4" color="orange" icon="error" size="32" />
                    <app-text size="small">
                        <b>Please note:</b>
                        Where applicable, this can change the Pass status for all Users in your Organisation, and can also create Passes for Users who
                        have not yet applied.
                        <br />
                        <i>Please check your selection before updating.</i>
                    </app-text>
                </div>

                <!--Status - From-->
                <div class="mt-4">
                    <app-text size="normal-bold">Change all</app-text>
                    <app-form-field form-type="select" :items="getSetAllPassesFromOptions()" item-text="value" v-model="setAllPassesFrom" />
                </div>

                <!--Status - To-->
                <div class="mt-4">
                    <app-text size="normal-bold">To</app-text>
                    <app-form-field
                        form-type="select"
                        :items="['Approved', 'Pending', 'Rejected', 'Reserved', 'Not Applied']"
                        item-text="value"
                        v-model="setAllPassesTo"
                    />
                </div>

                <div v-if="setAllPassesFrom && setAllPassesTo">
                    <v-divider class="mt-4" />

                    <!--If selections are the same-->
                    <app-text v-if="setAllPassesFrom === setAllPassesTo" class="mt-4" color="red">
                        You cannot set <b>{{ setAllPassesFrom }}</b> to <b>{{ setAllPassesTo }}</b
                        >.
                    </app-text>

                    <div v-else>
                        <app-text class="mt-4">
                            Are you sure you want to change all from <b>{{ setAllPassesFrom }}</b> to <b>{{ setAllPassesTo }}</b
                            >?
                        </app-text>

                        <ul>
                            <li v-if="setAllPassesFrom === 'Not Applied'">
                                <app-text class="mt-4" color="red"> This will also create Passes for these Users </app-text>
                            </li>
                        </ul>

                        <!--Buttons-->
                        <div class="d-flex justify-space-between mt-4">
                            <!--No-->
                            <app-btn @click.native="isSetAllDialogVisible = false" color="grey" icon="cancel" label="No" />

                            <!--Yes-->
                            <app-btn @click.native="updatePassesFromConfigurator" color="green" icon="success" label="Yes" />
                        </div>
                    </div>
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'MediaAccreditationPassForm',

    props: ['accreditationData', 'selectedUser'],

    data: () => ({
        isLoading: true,
        isRemovedUsersDialogVisible: false,
        isSetAllDialogVisible: false,
        organisationUsers: {},
        originalUsersAndStatuses: '',
        removedUserNames: [],
        selectedUsers: [],
        userHolderTypes: {},
        userOrganisations: {},
        userErrors: {},
        holderTypeOptions: ['Rights Holder', 'Non-Rights Holder'],
        statusOptions: [
            { value: 'Approved', color: 'green', icon: 'success' },
            { value: 'Pending', color: 'orange', icon: 'pending' },
            { value: 'Rejected', color: 'red', icon: 'cancel' },
            { value: 'Reserved', color: 'blue', icon: 'reserved' }
        ],
        usersAndStatuses: [],
        setAllPassesFrom: '',
        setAllPassesTo: ''
    }),

    computed: {
        computedTableData() {
            const t = this
            let tableData = []

            // Get passes data and remove duplicates by userId
            const uniqueUsers = new Map()
            const passesData = t.$props.accreditationData?.passesData || []

            passesData.forEach((item) => {
                // Check if we have the required data
                if (!item?.userData?.entityId) {
                    console.warn('Missing userData or entityId:', item)
                    return
                }

                const userId = item.userData.entityId
                // Only add if it's a new unique user
                if (!uniqueUsers.has(userId)) {
                    // Ensure we have valid passData or create default
                    const passData = item.passData || {
                        accreditationPassStatus: 'Not Applied',
                        accreditationPassEventId: t.$props.accreditationData?.eventData?.entityId,
                        accreditationPassOrganisationId: [item.userData.userOrganisation],
                        accreditationPassUserId: userId,
                        accreditationPassHolderType: '',
                        accreditationPassPrinted: false
                    }

                    // Create consistent item structure
                    const formattedItem = {
                        userData: item.userData,
                        passData: passData,
                        fileData: item.fileData || null,
                        organisationData: item.organisationData || {
                            entityId: Array.isArray(item.userData.userOrganisation)
                                ? item.userData.userOrganisation[0]
                                : item.userData.userOrganisation
                        }
                    }

                    uniqueUsers.set(userId, formattedItem)
                }
            })

            tableData = Array.from(uniqueUsers.values())

            // Remove selected user from the list if present
            if (t.$props.selectedUser?.userData?.entityId) {
                tableData = tableData.filter((item) => item.userData.entityId !== t.$props.selectedUser.userData.entityId)
            }

            // Sort by username
            tableData.sort((a, b) => {
                const nameA = a.userData.userName || ''
                const nameB = b.userData.userName || ''
                return nameA.localeCompare(nameB)
            })

            return tableData
        },
        /**
         * Computed Is Button Disabled
         *
         * Return true if the selectedUsers array is the same as the original, false otherwise.
         *
         * @returns {boolean}
         */
        computedIsButtonDisabled() {
            const t = this
            let isDisabled

            const ORIGINAL_USERS_AND_STATUSES = t.originalUsersAndStatuses
            const USERS_AND_STATUSES = t.usersAndStatuses

            // If the strings are the same (i.e. no changes have been made), disable the button
            isDisabled = ORIGINAL_USERS_AND_STATUSES === JSON.stringify(USERS_AND_STATUSES)

            return isDisabled
        },

        /**
         * Computed Users
         *
         * Return an array of Users from the selected User's Organisation.
         * The array is sorted into two groups: Users who have applied for a Pass, and Users who have not applied for a Pass.
         *
         * @returns {*[]}
         */
        computedUsers() {
            const t = this
            if (!Array.isArray(t.organisationUsers)) {
                console.warn('organisationUsers is not an array:', t.organisationUsers)
                return []
            }

            // Get users with pass status
            const usersApplied = t.organisationUsers.filter(
                (item) => item.passData?.accreditationPassStatus && item.passData.accreditationPassStatus !== 'Not Applied'
            )

            // Get users without pass status
            const usersNotApplied = t.organisationUsers.filter(
                (item) => !item.passData?.accreditationPassStatus || item.passData.accreditationPassStatus === 'Not Applied'
            )

            // Combine and remove selected user if present
            let tableData = [...usersApplied, ...usersNotApplied]

            if (t.$props.selectedUser?.userData?.entityId) {
                tableData = tableData.filter((item) => item.userData.entityId !== t.$props.selectedUser.userData.entityId)
            }

            return tableData
        }
    },

    methods: {
        isSelected(item) {
            return this.usersAndStatuses.some((u) => u.userData.entityId === item.userData.entityId)
        },
        /**
         * Confirm Delete Passes
         *
         * On confirming the removal of passes, emit the savePasses event back to the parent.
         */
        confirmDeletePasses() {
            const t = this

            t.isRemovedUsersDialogVisible = false

            t.$emit('emitSavePasses', t.selectedUsers)
        },

        /**
         * Get Set All Passes 'From' Options
         *
         * Return an array of pass statuses that are currently in use.
         *
         * @returns {string[]}
         */
        getSetAllPassesFromOptions() {
            const t = this
            let options = ['Approved', 'Pending', 'Rejected', 'Reserved', 'Not Applied']

            // Populate the options array with the pass statuses that are currently in use
            options = options.filter((item) => t.usersAndStatuses.find((u) => u.passStatus === item))

            return options
        },

        getAvailableOrganisations(userData) {
            const userOrgs = Array.isArray(userData.userOrganisation) ? userData.userOrganisation : [userData.userOrganisation]

            return userOrgs.map((orgId) => ({
                text: this.$props.accreditationData?.organisations?.[orgId] || orgId,
                value: orgId
            }))
        },
        /**
         * Get Status Config
         *
         * Return the colour and icon for the pass status.
         *
         * @returns {{color: string, icon: string}}
         * @param status {String} - The pass status
         */
        getStatusConfig(status) {
            let color
            let icon

            switch (status) {
                case 'Approved':
                    color = 'green'
                    icon = 'success'
                    break
                case 'Rejected':
                    color = 'red'
                    icon = 'cancel'
                    break
                case 'Pending':
                    color = 'orange'
                    icon = 'pending'
                    break
                case 'Reserved':
                    color = 'blue'
                    icon = 'reserved'
                    break
                default:
                    color = 'grey9'
                    icon = 'help'
                    break
            }

            return { color, icon }
        },

        /**
         * Get User Status
         *
         * Return the pass status for the selected user.
         *
         * @param user {Object} - The pass object {fileData{}, passData{}, userData{}}
         * @returns {string|String|*}
         */
        getUserStatus(user) {
            return this.usersAndStatuses.find((u) => u.userData.entityId === user.userData.entityId)?.passStatus
        },

        /**
         * Handle User Selection
         *
         * Add or remove the selectedUser from the selectedUsers array.
         * If all users are selected, check the "Select All" checkbox.
         * If all users are not selected, uncheck the "Select All" checkbox.
         *
         * @param item {Object} - The pass object {fileData{}, passData{}, userData{}}
         */
        handleUserSelection(item) {
            const t = this

            // If the user is already selected, remove them from the array
            if (t.selectedUsers.includes(item.userData.entityId)) t.selectedUsers = t.selectedUsers.filter((user) => user !== item.userData.entityId)
            // Otherwise, add them to the array
            else t.selectedUsers.push(item.userData.entityId)
        },

        /**
         * Handle Status Selection
         *
         * Update the pass status for the selected user, to update the UI.
         *
         * @param user {Object} - The pass object {fileData{}, passData{}, userData{}}
         * @param status {String} - The new pass status
         */
        handleStatusSelection(user, status) {
            const t = this
            const userId = user.userData.entityId

            // Update the pass status for the selected user
            const userStatus = t.usersAndStatuses.find((item) => item.userData.entityId === userId)
            if (userStatus) {
                userStatus.passStatus = status
            }

            // Make sure the user has holder type and organization when status changes
            if (!t.userHolderTypes[userId]) {
                t.$set(t.userHolderTypes, userId, user.passData?.accreditationPassHolderType || user.userData.userHolderType || '')
            }

            if (!t.userOrganisations[userId]) {
                const orgId = Array.isArray(user.passData?.accreditationPassOrganisationId)
                    ? user.passData.accreditationPassOrganisationId[0]
                    : user.passData?.accreditationPassOrganisationId ||
                      (Array.isArray(user.userData.userOrganisation) ? user.userData.userOrganisation[0] : user.userData.userOrganisation)

                t.$set(t.userOrganisations, userId, orgId || '')
            }
        },

        /**
         * Save Passes
         *
         * Compare the original and updated arrays.
         * If there are updated items, emit the savePasses event back to the parent.
         */
        savePasses() {
            const t = this

            // If the button is disabled, do nothing
            if (t.computedIsButtonDisabled) return

            const ORIGINAL_USERS_AND_STATUSES = JSON.parse(t.originalUsersAndStatuses)
            const USERS_AND_STATUSES = t.usersAndStatuses
            let updatedItems = []

            // Compare the original and updated arrays
            USERS_AND_STATUSES.forEach((currentUserStatus) => {
                // Find the original equivalent to the current currentUserStatus
                const ORIGINAL_USER_STATUS = ORIGINAL_USERS_AND_STATUSES.find((o) => o.userData.entityId === currentUserStatus.userData.entityId)

                // If the pass status has changed, add the user to the updatedItems array
                if (ORIGINAL_USER_STATUS && JSON.stringify(currentUserStatus) !== JSON.stringify(ORIGINAL_USER_STATUS)) {
                    updatedItems.push(currentUserStatus)
                }
            })

            // If there are updated items, emit the savePasses event back to the parent
            if (updatedItems.length) {
                t.$emit('emitSavePasses', {
                    usersAndStatuses: updatedItems,
                    organisationId: t.$props.selectedUser.organisationData.entityId
                })
            }
        },

        /**
         * Update Passes From Configurator
         *
         * Sort and call to update the pass statuses for all selected Users in the Organisation.
         */
        updatePassesFromConfigurator() {
            const t = this
            const ALL_FROM = t.setAllPassesFrom
            const ALL_TO = t.setAllPassesTo
            const USERS_AND_STATUSES = t.usersAndStatuses

            USERS_AND_STATUSES.forEach((item) => {
                if (item.passStatus === ALL_FROM) item.passStatus = ALL_TO
            })

            t.isSetAllDialogVisible = false
        }
    },

    /**
     * Mounted
     *
     * Get the data for the selected User's Organisation.
     * Create a new array of objects with the User data and Pass status.
     * This is used to display the User data and Pass status in the UI's Status Buttons.
     */
    mounted() {
        const t = this
        t.isLoading = true

        try {
            // Get the data for the selected User's Organisation
            const organisationId =
                t.$props.selectedUser?.organisationData?.entityId ||
                t.$props.selectedUser?.passData?.accreditationPassOrganisationId?.[0] ||
                t.$props.selectedUser?.userData?.userOrganisation?.[0]

            if (!organisationId) {
                console.error('No organization ID found for user:', t.$props.selectedUser)
                return
            }

            // Find the organisation data
            t.organisationUsers = t.$props.accreditationData.passesData || []

            // Initialize holder types and organizations for all users
            t.organisationUsers.forEach((item) => {
                if (item.userData?.entityId) {
                    // Initialize holder type
                    t.$set(
                        t.userHolderTypes,
                        item.userData.entityId,
                        item.passData?.accreditationPassHolderType || item.userData.userHolderType || ''
                    )

                    // Initialize organisation
                    const orgId = Array.isArray(item.passData?.accreditationPassOrganisationId)
                        ? item.passData.accreditationPassOrganisationId[0]
                        : item.passData?.accreditationPassOrganisationId ||
                          (Array.isArray(item.userData.userOrganisation) ? item.userData.userOrganisation[0] : item.userData.userOrganisation)

                    t.$set(t.userOrganisations, item.userData.entityId, orgId || '')

                    // Initialize error objects
                    t.$set(t.userErrors, item.userData.entityId, {
                        eventHolderType: false,
                        eventHolderTypeErrorMessage: '',
                        organisation: false,
                        organisationErrorMessage: ''
                    })
                }
            })

            // Create the users and statuses array, preserving existing pass data
            t.usersAndStatuses = t.organisationUsers
                .map((item) => ({
                    userData: item.userData,
                    passStatus: item.passData?.accreditationPassStatus || 'Not Applied',
                    passHolderType: item.passData?.accreditationPassHolderType || item.userData.userHolderType,
                    organisations: item.passData?.accreditationPassOrganisationId || item.userData.userOrganisation
                }))
                .filter((item) => item.userData?.entityId)

            // Initialize selected user's data specifically
            if (t.$props.selectedUser?.userData?.entityId) {
                const userId = t.$props.selectedUser.userData.entityId

                // Set holder type for selected user
                t.$set(
                    t.userHolderTypes,
                    userId,
                    t.$props.selectedUser.passData?.accreditationPassHolderType || t.$props.selectedUser.userData.userHolderType || ''
                )

                // Set organization for selected user
                const selectedUserOrgId = Array.isArray(t.$props.selectedUser.passData?.accreditationPassOrganisationId)
                    ? t.$props.selectedUser.passData.accreditationPassOrganisationId[0]
                    : t.$props.selectedUser.passData?.accreditationPassOrganisationId ||
                      (Array.isArray(t.$props.selectedUser.userData.userOrganisation)
                          ? t.$props.selectedUser.userData.userOrganisation[0]
                          : t.$props.selectedUser.userData.userOrganisation)

                t.$set(t.userOrganisations, userId, selectedUserOrgId || '')
            }

            // Pre-select status buttons based on existing data
            t.selectedStatus = t.$props.selectedUser.passData?.accreditationPassStatus || 'Not Applied'

            // Store original state for comparison
            t.originalUsersAndStatuses = JSON.stringify(t.usersAndStatuses)
        } catch (error) {
            console.error('Error in mounted:', error)
            t.$sharedState.errorMessage = 'Error initializing form data'
        } finally {
            t.isLoading = false
        }
    }
}
</script>

<style scoped>
.fixed-save-button {
    position: fixed;
    bottom: 48px;
    right: 48px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
}
</style>
